/**
 * Feature E02-F04-T05 — Stage-1 Pre-Kickoff save api-client + Query hooks.
 *
 * Two persistence paths the Stage-1 screen needs (1:1 with the legacy
 * `saveBriefField` + the Position Type dropdown):
 *   - a brief-field edit (Industry/Type/Offices/Products/Country/Scope/
 *     Requirements/Role-goal) is saved into the `analysis` artifact via
 *     PUT /projects/:id/artifacts/analysis (save_stage4_artifact) — the whole
 *     merged content is re-persisted (legacy verbatim `content` save);
 *   - the Position Type selection is persisted to `projects.position_type_id` via
 *     PATCH /projects/:id (update_project) — `position_type_id` is a first-class
 *     key of the universal updates bag.
 *
 * Both invalidate the project's detail card so the re-read reflects the saved
 * values (DB = source of truth).
 */
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { saveArtifactResponseSchema } from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';
import { qk } from '../api/queryKeys.js';
import { ANALYSIS_ARTIFACT_TYPE } from './stage1Model.js';

/** Save the merged analysis content (PUT /projects/:id/artifacts/analysis). */
export async function saveAnalysisArtifact(args: {
  projectId: number;
  content: Record<string, unknown>;
}): Promise<void> {
  const result = await apiClient.put<unknown>(
    `/projects/${args.projectId}/artifacts/${ANALYSIS_ARTIFACT_TYPE}`,
    { content: args.content },
    { command: 'save_stage4_artifact' },
  );
  saveArtifactResponseSchema.parse(unwrap(result));
}

/** Persist the position-type selection (PATCH /projects/:id, position_type_id). */
export async function savePositionType(args: {
  projectId: number;
  positionTypeId: number | null;
}): Promise<void> {
  const result = await apiClient.patch<unknown>(
    `/projects/${args.projectId}`,
    { updates: { position_type_id: args.positionTypeId } },
    { command: 'update_project' },
  );
  unwrap(result);
}

export interface SaveBriefVars {
  readonly projectId: number;
  readonly content: Record<string, unknown>;
}

/** Brief-field save mutation (writes the analysis artifact). */
export function useSaveBriefField(): UseMutationResult<void, ApiResultError, SaveBriefVars> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiResultError, SaveBriefVars>({
    mutationFn: ({ projectId, content }) => saveAnalysisArtifact({ projectId, content }),
    onSuccess: (_d, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
    },
  });
}

export interface SavePositionTypeVars {
  readonly projectId: number;
  readonly positionTypeId: number | null;
}

/** Position-type save mutation (writes projects.position_type_id). */
export function useSavePositionType(): UseMutationResult<
  void,
  ApiResultError,
  SavePositionTypeVars
> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiResultError, SavePositionTypeVars>({
    mutationFn: ({ projectId, positionTypeId }) => savePositionType({ projectId, positionTypeId }),
    onSuccess: (_d, { projectId }) => {
      void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
    },
  });
}
